import { useMemo, useState } from 'react'
import { AlertTriangle } from 'lucide-react'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { toast } from '@/stores/useToastStore'
import { getErrorMessage } from '@/utils/getErrorMessage'
import { parseDagInput } from './DagCellRenderer'
import type {
  DagColumnMeta,
  BulkColumnEditRequest,
  BulkColumnEditResponse,
} from '@/types/dag'

type UiOperation = 'set' | 'inc_pct' | 'dec_pct' | 'inc_amt' | 'dec_amt'

const UI_OPERATIONS: {
  id: UiOperation
  label: string
  operation: 'set' | 'percent' | 'flat'
  sign: 1 | -1
}[] = [
  { id: 'set', label: 'Set to', operation: 'set', sign: 1 },
  { id: 'inc_pct', label: 'Increase by %', operation: 'percent', sign: 1 },
  { id: 'dec_pct', label: 'Decrease by %', operation: 'percent', sign: -1 },
  { id: 'inc_amt', label: 'Increase by amount', operation: 'flat', sign: 1 },
  { id: 'dec_amt', label: 'Decrease by amount', operation: 'flat', sign: -1 },
]

interface BulkColumnEditModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  columnMeta: DagColumnMeta | null
  onSubmit: (
    request: BulkColumnEditRequest,
    onSuccess: (data: BulkColumnEditResponse) => void,
    onError: (error: Error) => void
  ) => void
  isSubmitting: boolean
}

export function BulkColumnEditModal({
  open,
  onOpenChange,
  columnMeta,
  onSubmit,
  isSubmitting,
}: BulkColumnEditModalProps) {
  const [uiOperationId, setUiOperationId] = useState<UiOperation>('set')
  const [rawValue, setRawValue] = useState('')
  const [failingMessage, setFailingMessage] = useState<string | null>(null)

  const handleOpenChange = (newOpen: boolean) => {
    if (newOpen) {
      setUiOperationId('set')
      setRawValue('')
      setFailingMessage(null)
    }
    onOpenChange(newOpen)
  }

  const uiOperation =
    UI_OPERATIONS.find(op => op.id === uiOperationId) ?? UI_OPERATIONS[0]

  // Parse the entered value into the raw units the request needs.
  const parsedValue = useMemo(() => {
    if (!columnMeta || rawValue.trim() === '') return null

    if (uiOperation.operation === 'set') {
      const parsed = parseDagInput(rawValue, columnMeta.format)
      return typeof parsed === 'number' && !isNaN(parsed) ? parsed : null
    }

    if (uiOperation.operation === 'flat') {
      // Parse in the column's own display units (e.g. a percent-format
      // column's "5" means +5 percentage points, i.e. +0.05 raw) so the
      // sign convention stacks cleanly on top of parseDagInput's cleanup.
      const parsed = parseDagInput(rawValue, columnMeta.format)
      return typeof parsed === 'number' && !isNaN(parsed)
        ? uiOperation.sign * parsed
        : null
    }

    // percent operation: value is always a bare percentage magnitude,
    // independent of the column's own format.
    const num = parseFloat(rawValue.replace(/[%\s]/g, ''))
    return isNaN(num) ? null : uiOperation.sign * num
  }, [rawValue, uiOperation, columnMeta])

  const backendOperation = uiOperation.operation

  const canSubmit = !!columnMeta && parsedValue !== null && !isSubmitting

  const handleConfirm = () => {
    if (!columnMeta || parsedValue === null) return

    const request: BulkColumnEditRequest = {
      column_id: columnMeta.id,
      operation: backendOperation,
      value: parsedValue,
      apply_to_all_rows: true,
    }

    onSubmit(
      request,
      data => {
        const skippedCount = data.skipped.length
        if (skippedCount > 0) {
          toast.warning(
            `Updated ${data.updated} SKU${data.updated === 1 ? '' : 's'}, ` +
              `skipped ${skippedCount} with no current value`
          )
        } else {
          toast.success(
            `Updated ${data.updated} SKU${data.updated === 1 ? '' : 's'}`
          )
        }
        handleOpenChange(false)
      },
      error => {
        setFailingMessage(
          getErrorMessage(error, `Failed to update ${columnMeta.label}`)
        )
      }
    )
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Update {columnMeta?.label ?? 'Column'}</DialogTitle>
          <DialogDescription>
            This will apply to every SKU in this plan version.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label>Change</Label>
            <Select
              value={uiOperationId}
              onValueChange={value => {
                setUiOperationId(value as UiOperation)
                setFailingMessage(null)
              }}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {UI_OPERATIONS.map(op => (
                  <SelectItem key={op.id} value={op.id}>
                    {op.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label>Value</Label>
            <Input
              type="text"
              inputMode="decimal"
              placeholder={uiOperation.operation === 'percent' ? '5' : '0'}
              value={rawValue}
              onChange={e => {
                setRawValue(e.target.value)
                setFailingMessage(null)
              }}
            />
          </div>
        </div>

        {failingMessage && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{failingMessage}</AlertDescription>
          </Alert>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={!canSubmit}>
            {isSubmitting ? 'Applying...' : 'Apply'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
